import React from 'react';

import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PhoneIcon from '@mui/icons-material/Phone';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { WebsiteAnalysisResultsProps } from '../../services/types/onboarding';
import styles from './SetupOnboarding.module.scss';

/**
 * Component to display the results of website analysis for onboarding
 *
 * @param {WebsiteAnalysisResultsProps} props - Component props containing analysis data
 * @returns {JSX.Element} Website analysis results component
 */
const WebsiteAnalysisResults: React.FC<WebsiteAnalysisResultsProps> = ({ analysis, onContinue }) => {
  const { companyInfo, vehicleLinks, brands, contactInfo, additionalServices } = analysis;

  return (
    <div className={styles.setupContent}>
      {/* Header */}
      <Box className={styles.header}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
        <Typography variant="h4" component="h1" className={styles.title}>
          ¡Análisis Completado!
        </Typography>
        <Typography variant="subtitle1" className={styles.subtitle}>
          Hemos analizado tu sitio web y extraído la siguiente información
        </Typography>
      </Box>

      {/* Company Information */}
      <Card className={styles.setupCard}>
        <CardContent className={styles.cardContent}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <BusinessIcon color="primary" />
            <Typography variant="h6" component="h2">
              Información de la Empresa
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {companyInfo.name && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Nombre
                </Typography>
                <Typography variant="body1">{companyInfo.name}</Typography>
              </Grid>
            )}
            {companyInfo.description && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1">{companyInfo.description}</Typography>
              </Grid>
            )}
            {companyInfo.yearsOfExperience && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TimelapseIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Experiencia
                  </Typography>
                </Box>
                <Typography variant="body1">{companyInfo.yearsOfExperience}</Typography>
              </Grid>
            )}
            {companyInfo.businessHours && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Horarios de Atención
                </Typography>
                <Typography variant="body1">{companyInfo.businessHours}</Typography>
              </Grid>
            )}
            {companyInfo.additionalInfo && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Información Adicional
                </Typography>
                <Typography variant="body1">{companyInfo.additionalInfo}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Brands */}
      {brands.length > 0 && (
        <Card className={styles.setupCard}>
          <CardContent className={styles.cardContent}>
            <Typography variant="h6" component="h2" gutterBottom>
              Marcas Detectadas ({brands.length})
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {brands.map((brand) => (
                <Chip key={brand} label={brand} variant="outlined" color="primary" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Vehicle Links */}
      <Card className={styles.setupCard}>
        <CardContent className={styles.cardContent}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <LinkIcon color="primary" />
            <Typography variant="h6" component="h2">
              Enlaces de Vehículos
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* New Cars URLs */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Autos Nuevos ({vehicleLinks.newCarsUrls.length})
              </Typography>
              {vehicleLinks.newCarsUrls.length > 0 ? (
                vehicleLinks.newCarsUrls.map((url, index) => (
                  <Box key={url} mb={1}>
                    <Typography variant="caption" color="text.secondary">
                      {vehicleLinks.newCarsTexts[index] || 'Sin texto'}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', textDecoration: 'none', color: 'primary.main' }}
                    >
                      {url}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No se encontraron enlaces de autos nuevos
                </Typography>
              )}
            </Grid>

            {/* Used Cars URLs */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="secondary" gutterBottom>
                Autos Usados ({vehicleLinks.usedCarsUrls.length})
              </Typography>
              {vehicleLinks.usedCarsUrls.length > 0 ? (
                vehicleLinks.usedCarsUrls.map((url, index) => (
                  <Box key={url} mb={1}>
                    <Typography variant="caption" color="text.secondary">
                      {vehicleLinks.usedCarsTexts[index] || 'Sin texto'}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', textDecoration: 'none', color: 'secondary.main' }}
                    >
                      {url}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No se encontraron enlaces de autos usados
                </Typography>
              )}
            </Grid>

            {/* Brand Specific URLs */}
            {vehicleLinks.brandSpecificUrls.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Enlaces por Marca ({vehicleLinks.brandSpecificUrls.length})
                </Typography>
                <Grid container spacing={2}>
                  {vehicleLinks.brandSpecificUrls.map((brandUrl) => (
                    <Grid key={brandUrl.url} item xs={12} sm={6} md={4}>
                      <Box p={2} border={1} borderColor="divider" borderRadius={1}>
                        <Typography variant="subtitle2" color="primary">
                          {brandUrl.brand}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {brandUrl.text}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="a"
                          href={brandUrl.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none', color: 'primary.main', fontSize: '0.75rem' }}
                        >
                          {brandUrl.url}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className={styles.setupCard}>
        <CardContent className={styles.cardContent}>
          <Typography variant="h6" component="h2" gutterBottom>
            Información de Contacto
          </Typography>

          <Grid container spacing={3}>
            {/* General Contact */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Contacto General
              </Typography>
              {contactInfo.generalPhone && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PhoneIcon color="action" fontSize="small" />
                  <Typography variant="body2">{contactInfo.generalPhone}</Typography>
                </Box>
              )}
              {contactInfo.email && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <EmailIcon color="action" fontSize="small" />
                  <Typography variant="body2">{contactInfo.email}</Typography>
                </Box>
              )}
            </Grid>

            {/* Branches */}
            {contactInfo.branches.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Sucursales ({contactInfo.branches.length})
                </Typography>
                {contactInfo.branches.map((branch) => (
                  <Box key={branch.address} mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
                    {branch.name && (
                      <Typography variant="body2" fontWeight="medium">
                        {branch.name}
                      </Typography>
                    )}
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <LocationOnIcon color="action" fontSize="small" />
                      <Typography variant="body2">{branch.address}</Typography>
                    </Box>
                    {branch.phone && (
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <PhoneIcon color="action" fontSize="small" />
                        <Typography variant="body2">{branch.phone}</Typography>
                      </Box>
                    )}
                    {branch.purpose && (
                      <Typography variant="caption" color="text.secondary">
                        {branch.purpose}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Additional Services */}
      {additionalServices.length > 0 && (
        <Card className={styles.setupCard}>
          <CardContent className={styles.cardContent}>
            <Typography variant="h6" component="h2" gutterBottom>
              Servicios Adicionales ({additionalServices.length})
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {additionalServices.map((service) => (
                <Chip key={service} label={service} variant="outlined" color="secondary" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Continue Button */}
      {onContinue && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Button
            variant="contained"
            size="large"
            endIcon={<NavigateNextIcon />}
            onClick={onContinue}
            className={styles.continueButton}
          >
            Continuar: Configurar Enlaces de Inventario
          </Button>
        </Box>
      )}
    </div>
  );
};

export default WebsiteAnalysisResults;
